import React, { Component } from "react";
import PropTypes from "prop-types";
import { styled } from "@mui/material/styles";

import { equals } from "ramda";
import clsx from "clsx";

import MaterialLayer from "~/components/MaterialLayer";
import LinenCloth from "~/components/LinenCloth";

const Page = styled("div")`
  position: relative;
  width: ${props => (props.isDoublePage ? 50 : 100)}%;
  height: 100%;
  float: ${props => (props.isFirstPage ? "right" : "left")};
  background-image: url(${props => props.thumbnailPath});
  background-repeat: no-repeat;
  background-position: center;
  background-size: 100%;

  @media print {
    background-image: none;
    page-break-inside: avoid !important;
  }
`;

const Image = styled("img")`
  width: 100%;
  max-width: 100%;
  height: auto;
`;

function getPageClasses(isActivePage) {
  return clsx({
    page: true,
    "page--active": isActivePage
  });
}

class PageView extends Component {
  static whyDidYouRender = false;

  constructor(props) {
    super(props);
    this.maxPageViewScale = 0;
  }

  componentDidUpdate(prevProps) {
    if (!equals(this.props.pages, prevProps.pages)) {
      setTimeout(() => {
        this.props.zoomController.zoomToFitAgain();
        this.maxPageViewScale = 0;
      }, 0);
      setTimeout(() => {
        this.props.zoomController.zoomToFitAgain();
      }, 400);
    }
  }

  render() {
    const {
      bookId,
      pages,
      isMaterialVisible,
      isAnnotationsVisible,
      getThumbnailPath,
      getImagePath,
      zoomController,
      showAnnotations,
      showMaterials,
      pagesCount,
      absolutePage,
      pageWidth,
      pageHeight,
      viewerOrigin,
      isDrm
    } = this.props;

    const isSinglePage = pages.length === 1;
    const isDoublePage = pages.length === 2;

    return pages.map((page, idx) => {
      const isFirstPage = page === 1;
      const isLastPage = page === pagesCount;

      const isLeftPage = idx === 0;
      const isRightPage = idx === 1;

      this.maxPageViewScale = Math.max(this.maxPageViewScale, zoomController.getScale());

      const thumbnailPath = getThumbnailPath(page);
      const imagePath = getImagePath(page, this.maxPageViewScale);

      return (
        <Page
          className={getPageClasses(page === absolutePage)}
          key={`page_view_${isLeftPage ? "left" : "right"}`}
          isDoublePage={pages.length === 2}
          isFirstPage={page === 1}
          thumbnailPath={thumbnailPath}
        >
          <div style={{ position: "relative" }}>
            <Image className="lazyload" data-sizes="auto" data-src={imagePath} src={thumbnailPath} />

            {isDrm && showAnnotations && isAnnotationsVisible && (
              <LinenCloth
                viewerOrigin={viewerOrigin}
                key={`canvas_${page}_${bookId}`}
                bookId={bookId}
                page={page}
                zoomController={zoomController}
                pageWidth={pageWidth}
                pageHeight={pageHeight}
              />
            )}
          </div>

          {showMaterials && isMaterialVisible && (
            <MaterialLayer
              zoomController={zoomController}
              key={`material_layer_${page}`}
              page={page}
              isLeftPage={isLeftPage}
              isRightPage={isRightPage}
            />
          )}
        </Page>
      );
    });
  }
}

PageView.propTypes = {
  pagesCount: PropTypes.number.isRequired,
  isMaterialVisible: PropTypes.bool.isRequired,
  isAnnotationsVisible: PropTypes.bool.isRequired,
  bookId: PropTypes.string.isRequired,
  pages: PropTypes.array.isRequired,
  zoomController: PropTypes.object.isRequired,
  getThumbnailPath: PropTypes.func.isRequired,
  getImagePath: PropTypes.func.isRequired,
  showAnnotations: PropTypes.bool,
  showMaterials: PropTypes.bool,
  absolutePage: PropTypes.number,
  pageWidth: PropTypes.number,
  pageHeight: PropTypes.number,
  isDrm: PropTypes.bool,
  viewerOrigin: PropTypes.string
};

export default PageView;
